// Offline-Queue für Elevation-Pushes (Wave 14bq).
//
// Warum diese Datei existiert
// ---------------------------
// User-Report: "wenn ich am handy ein höhenprofil von einem trail erzeuge
// landet er nicht IM BACKEND, somit muss ich am anderen browser/gerät es
// wieder erzeugen. wenn ich hingegen ein crux erzeuge, sehe ich den crux auf
// allen geräten (gut)".
//
// Die Crux-Queue (lib/admin.ts) war das Goldstandard-Pattern: optimistisch
// lokal schreiben, dann pushen, bei Fehler in IDB parken, auf 'online'-
// Event retry'n. Dieses Modul ist die 1:1-Umsetzung für Elevation-Samples
// — Key-Schema und Flow sind bewusst parallel gehalten, damit man die
// beiden Queues mental zusammen behandeln kann.
//
// Unterschiede zur Crux-Queue:
//   - Kein Auth-Token nötig (POST /api/trails/:id/elevation-samples ist
//     public unauth; Rate-Limit kommt serverseitig über smoothingVersion-
//     Check + "server-sampled gewinnt"-Logik).
//   - Die gequeuete Payload ist nicht-inkrementell (nur der letzte Stand
//     pro Trail, analog cruxPoints), aber sie enthält das komplette
//     Sample-Array (100 floats ≈ 1.2 KB JSON) statt einer kleinen Liste.
//     Das ist OK — IDB-Quota im hunderte-MB-Bereich, und die Queue hat
//     typisch 1-3 Einträge.
//   - Bei "skipped" (Server hat schon server-sampled Samples) ist der
//     Push KEIN Fehler — der Eintrag darf raus aus der Queue, weil ein
//     Retry dasselbe Ergebnis hätte.
//   - 400er-Fehler (smoothing_version_mismatch, invalid_id, etc.) sind
//     unrecoverable → Eintrag rauswerfen, nicht endlos retry'n.

import { rawPushElevationSamples } from './api';
import { getDb } from './storage/db';

/** Custom-Event: die Queue hat sich geändert. Debug-UI kann mithören. */
export const ELEVATION_QUEUE_CHANGED_EVENT = 'adt:elevation-queue-changed';

function emitQueueChanged() {
  try {
    window.dispatchEvent(new CustomEvent(ELEVATION_QUEUE_CHANGED_EVENT));
  } catch {
    // No-op bei SSR / Tests ohne window.
  }
}

export interface EnqueueElevationInput {
  trailId: string;
  samples: number[];
  smoothingVersion: number;
  trackLengthMeters: number;
}

/**
 * Parkt eine pending Elevation-Payload in IDB. Idempotent pro trailId —
 * ein neuer Eintrag überschreibt den alten (last-write-wins, weil unsere
 * smoothingVersion+Sample-Count konstant sind, d. h. zwei Pushes für
 * denselben Trail sind inhaltlich äquivalent). Wirft nie — IDB-Fehler
 * werden geloggt aber nicht propagiert, sonst könnte ein IDB-Problem das
 * UI-Rendering verhindern.
 */
async function enqueuePending(
  input: EnqueueElevationInput,
  errorMessage: string,
): Promise<void> {
  try {
    const db = await getDb();
    const existing = await db.get('elevationQueue', input.trailId);
    await db.put('elevationQueue', {
      trailId: input.trailId,
      samples: input.samples,
      smoothingVersion: input.smoothingVersion,
      trackLengthMeters: input.trackLengthMeters,
      queuedAt: existing?.queuedAt ?? new Date().toISOString(),
      attempts: (existing?.attempts ?? 0) + 1,
      lastError: errorMessage,
    });
    emitQueueChanged();
  } catch (err) {
    console.warn('[elev-queue] enqueuePending failed', err);
  }
}

async function dequeuePending(trailId: string): Promise<void> {
  try {
    const db = await getDb();
    await db.delete('elevationQueue', trailId);
    emitQueueChanged();
  } catch (err) {
    console.warn('[elev-queue] dequeuePending failed', err);
  }
}

export interface PendingElevationItem {
  trailId: string;
  samples: number;          // Count, nicht das Array — spart UI-Payload
  queuedAt: string;
  attempts: number;
  lastError?: string;
}

/**
 * Für UI-Visibility (ProfilePage o. Ä.). Analog zu listPendingCruxItems().
 */
export async function listPendingElevationItems(): Promise<PendingElevationItem[]> {
  try {
    const db = await getDb();
    const items = await db.getAll('elevationQueue');
    return items.map((i) => ({
      trailId: i.trailId,
      samples: i.samples.length,
      queuedAt: i.queuedAt,
      attempts: i.attempts,
      lastError: i.lastError,
    }));
  } catch {
    return [];
  }
}

export interface EnqueueElevationPushResult {
  synced: boolean;
  skipped?: boolean;
  reason?: string;
  error?: string;
}

/**
 * Hauptfunktion aus dem useTrailElevation-Hook: erst einmal den Push
 * probieren, bei Erfolg direkt synced=true zurück. Bei Fehler queuen und
 * synced=false zurück. Wirft niemals, damit saveCachedSrtm() nicht knallen
 * kann. Analog zu appendCruxPoint() aber ohne die "lokal speichern"-
 * Doppelung (das macht saveCachedSrtm selbst schon).
 */
export async function enqueueElevationPush(
  input: EnqueueElevationInput,
): Promise<EnqueueElevationPushResult> {
  try {
    const res = await rawPushElevationSamples(
      input.trailId,
      input.samples,
      input.smoothingVersion,
      input.trackLengthMeters,
    );
    // Server hat angenommen (stored oder skipped) — in beiden Fällen darf
    // ein eventuell noch hängender Queue-Eintrag raus (z. B. nach manuellem
    // Rebuild der auf dem ersten Versuch durchging).
    await dequeuePending(input.trailId);
    return { synced: true, skipped: res.skipped, reason: res.reason };
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    // 400er aus dem Server sind nicht-retry-bar — Queue würde endlos denselben
    // Fehler sehen. Wir queuen deshalb NUR bei Netz-/5xx-Fehlern.
    if (isUnrecoverable(msg)) {
      console.warn(`[elev-queue] unrecoverable push error, not queuing: ${msg}`);
      return { synced: false, error: msg };
    }
    await enqueuePending(input, msg);
    console.info(`[elev-queue] queued for retry (${msg}): ${input.trailId}`);
    return { synced: false, error: msg };
  }
}

/**
 * 400er-Fehler vom Backend: Client-Config ist kaputt (smoothing-version-
 * mismatch, invalid id), kein Retry sinnvoll. 404 trail_not_found ist auch
 * hartes Ende — Trail existiert serverseitig nicht, keine Chance auf Self-
 * Healing. Alles andere (Netz weg, 5xx, timeout) = retry-fähig.
 */
function isUnrecoverable(msg: string): boolean {
  return /\b(400|404):/.test(msg) || /invalid_trail_id/.test(msg);
}

export interface FlushElevationResult {
  tried: number;
  synced: number;
  stillPending: number;
  dropped: number;        // unrecoverable-Fehler, aus Queue rausgeworfen
}

/**
 * Drain der elevationQueue. Wird aus App-Root gerufen (Mount + 'online'-
 * Event). Leere Queue = no-op, stumm.
 *
 * Sequentiell statt parallel: auf flakeligem Netz will man nicht 10
 * Pushes simultan raushauen und dann mit 10 Timeouts dastehen. Ein-nach-
 * dem-anderen ist robuster; der erste Fehler zeigt auch, dass der Rest
 * wahrscheinlich auch failen wird → nächste 'online'-Runde.
 */
export async function flushElevationQueue(): Promise<FlushElevationResult> {
  let tried = 0;
  let synced = 0;
  let stillPending = 0;
  let dropped = 0;

  let items: Array<{
    trailId: string;
    samples: number[];
    smoothingVersion: number;
    trackLengthMeters: number;
  }> = [];
  try {
    const db = await getDb();
    const all = await db.getAll('elevationQueue');
    items = all.map((i) => ({
      trailId: i.trailId,
      samples: i.samples,
      smoothingVersion: i.smoothingVersion,
      trackLengthMeters: i.trackLengthMeters,
    }));
  } catch (err) {
    console.warn('[elev-queue] flushElevationQueue: couldn\'t read queue', err);
    return { tried: 0, synced: 0, stillPending: 0, dropped: 0 };
  }
  if (items.length === 0) return { tried: 0, synced: 0, stillPending: 0, dropped: 0 };

  for (const item of items) {
    tried += 1;
    try {
      await rawPushElevationSamples(
        item.trailId,
        item.samples,
        item.smoothingVersion,
        item.trackLengthMeters,
      );
      await dequeuePending(item.trailId);
      synced += 1;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (isUnrecoverable(msg)) {
        // Rauswerfen — die Payload würde auch in 24h Retry noch failen.
        await dequeuePending(item.trailId);
        dropped += 1;
        console.warn(`[elev-queue] dropped unrecoverable queue entry: ${item.trailId} (${msg})`);
      } else {
        // Re-enqueue mit inkrementiertem attempts-Counter.
        await enqueuePending(item, msg);
        stillPending += 1;
      }
    }
  }

  if (synced > 0 || dropped > 0) {
    console.info(
      `[elev-queue] flush: ${synced}/${tried} synced, ${stillPending} pending, ${dropped} dropped`,
    );
  }
  return { tried, synced, stillPending, dropped };
}
